import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  useCurrentConversationIdValue,
  useSetConversationReadonly,
  useSetCurrentConversationIdValue,
} from './conversation';
import { getTalkInfo, updateConversationV2 } from 'services/api/conversation.convList';
import { message } from 'antd';
import { shouldAcquireControlConversationMapRef } from './im/useIM';
import { useSetSearchMsgSelectedSendTime } from './searchMsgSelectedMsg';
import { useSetConversation, useGetConversationById } from './lruConversation';

interface Props extends Pick<Conversation.Conversation, 'conversationId'> {
  visible?: boolean;
  forceAcquireControlReason?: Conversation.ForceAcquireControlReason;
  readonly?: boolean;
  searchMsgId?: string;
  searchMsgSendTime?: string;
  searchBlock?: ScrollLogicalPosition;
}

function useSecondConversation(props: Props) {
  const {
    conversationId,
    visible = true,
    forceAcquireControlReason,
    readonly = false,
    searchMsgId,
    searchMsgSendTime,
    searchBlock,
  } = props;

  const { t } = useTranslate();

  const [loading, setLoading] = useState(false);

  const getConversationById = useGetConversationById();
  const conversation = getConversationById(conversationId);

  const setConversation = useSetConversation();

  const currentConversationId = useCurrentConversationIdValue();

  const currentConversationIdRef = useRef(currentConversationId);

  const setCurrentConversationId = useSetCurrentConversationIdValue();

  const setConversationReadonly = useSetConversationReadonly();

  const setSearchMsgSelectedSendTime = useSetSearchMsgSelectedSendTime();

  const getConversation = useCallback(
    async (conversationId: string) => {
      if (conversationId === '0') {
        return;
      }

      try {
        setLoading(true);
        await updateConversationV2({ conversationId, acquireControl: true });
        const conversation = await getTalkInfo({ conversationId });
        if (conversation) {
          setConversation(conversationId, conversation);
        }
      } catch (err) {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      } finally {
        setLoading(false);
      }
    },
    [setConversation, t],
  );

  const refsCurrent = {
    conversationId,
    conversation,
    setCurrentConversationId,
    getConversation,
    setConversationReadonly,
    setSearchMsgSelectedSendTime,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { conversation, setCurrentConversationId, getConversation },
    } = refs;

    if (!visible) {
      return;
    }

    setCurrentConversationId(conversationId);

    if (!conversation) {
      getConversation(conversationId);
    }
  }, [visible, conversationId]);

  useEffect(() => {
    if (!visible) {
      return () => {};
    }

    if (forceAcquireControlReason) {
      shouldAcquireControlConversationMapRef.ref[conversationId] = forceAcquireControlReason;
    }

    return () => {
      delete shouldAcquireControlConversationMapRef.ref[conversationId];
    };
  }, [visible, forceAcquireControlReason, conversationId]);

  useEffect(() => {
    if (!visible) {
      return () => {};
    }

    refs.current.setSearchMsgSelectedSendTime({
      msgId: searchMsgId || '',
      sendTime: searchMsgSendTime || '',
      block: searchBlock,
    });

    return () => {
      const { current: currentConversationId } = currentConversationIdRef;

      const {
        current: { setCurrentConversationId, setSearchMsgSelectedSendTime },
      } = refs;

      setCurrentConversationId(currentConversationId);

      setSearchMsgSelectedSendTime({ msgId: '', sendTime: '' });
    };
  }, [visible, searchMsgId, searchMsgSendTime, searchBlock]);

  useEffect(() => {
    if (!visible) {
      return () => {};
    }

    refs.current.setConversationReadonly(readonly);

    return () => refs.current.setConversationReadonly(false);
  }, [readonly, visible]);

  return {
    loading,
    conversation,
    retry: getConversation,
  };
}

export default useSecondConversation;
